import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { translationService, onApiIdle } from '../services/api';
import { useLocation } from 'react-router-dom';
import { mapLanguageResponse, getPopularLanguages } from '../utils/languageMapping';
import i18n from '../i18n';
import { getStaticTranslation, getStaticTemplateMatch, normalizeLang } from '../translations/registerStaticTranslations';

// Exported (additive, nothing else changes) so a component can read the
// language WITHOUT the throwing `useTranslation` above. `useTranslation` is the
// right hook for anything that needs the provider; a block that must also
// render in a unit test or a measurement harness, where no provider exists,
// reads this context directly and falls back to English. Wrapping
// `useTranslation` in a try/catch is not an option: React's rules-of-hooks
// treats that as a conditional hook call and the production build fails on it.
export const TranslationContext = createContext();

const CACHE_KEY = 'translation_cache';
const LANGUAGE_KEY = 'selected_language';
const CACHE_VERSION_KEY = 'translation_cache_version';
const CACHE_TIMESTAMP_KEY = 'translation_cache_timestamp';
const CACHE_VERSION = '1.0.0'; // Update this when app version changes
const CACHE_EXPIRY_HOURS = 24;
const MAX_BATCH_SIZE = 2000; // Max strings per API request
const MAX_CACHE_SIZE = 10000; // Max cached translation pairs
const DEBOUNCE_DELAY = 300; // Debounce translation requests
// Languages that only support static bundles; dynamic API should not be called
const STATIC_ONLY_LANGS = new Set(['hr']);
// Text-bearing attributes translated like text nodes (value attributes are never touched)
const TRANSLATABLE_ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt'];

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [translationCache, setTranslationCache] = useState({});
  const [translationProgress, setTranslationProgress] = useState(0);
  const debounceTimeoutRef = useRef(null);
  // Debounce slot for runs scoped to a SUBTREE (rootElement !== body). A scoped
  // run must never occupy — and therefore never cancel — the slot of a pending
  // FULL-PAGE run: the home page's transfer-window widget re-passes itself
  // through translatePage (forced, 150ms after its data lands), and when that
  // call shared the single slot it cleared the whole-page timeout that a
  // language switch had just scheduled. The switch then translated the widget's
  // ~10 nodes and nothing else — the top-bar navigation kept the previous
  // language, which is Marin's 2026-08-11 "Requests / My Players / Inbox hängt"
  // report. A full-page run still clears BOTH slots (it covers every subtree).
  const scopedDebounceTimeoutRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const pageTranslationTimeoutRef = useRef(null);
  const lastTranslatedPathRef = useRef('');
  const isInitializedRef = useRef(false);
  const location = useLocation();
  const mutationDebounceRef = useRef(null);
  const failedTranslationKeysRef = useRef(new Set());
  const isTranslatingRef = useRef(false);
  const lastRunAtRef = useRef(0);
  const MIN_RUN_INTERVAL_MS = 1500;
  const currentLanguageRef = useRef('en');
  // Mirror of the translationCache STATE, so the translation callbacks can read
  // the freshest cache without depending on the state value. With the state in
  // their dependency arrays, every cache write minted a NEW translatePage
  // identity; the transfer-window widget's effect lists translatePage as a
  // dependency, re-fired on it, ran a forced scoped translation, wrote to the
  // cache again — a self-sustaining ~450ms loop that refreshed lastRunAtRef on
  // every lap and so threw every unforced full-page run into the throttle,
  // forever (and, for non-static-only languages, re-posted the same widget
  // strings to /users/translate/ each lap). Writes land in the ref FIRST,
  // then in the state; the state stays the single source for persistence and
  // for the cacheSize the UI shows.
  const translationCacheRef = useRef({});
  const originalTextMapRef = useRef(new Map());
  const translatedNodesRef = useRef(new Set());
  // element -> Map(attrName -> original English value), mirrors originalTextMapRef for attributes
  const originalAttrMapRef = useRef(new Map());
  // element -> Map(attrName -> value we last wrote), used to ignore our own attribute mutations
  const lastWrittenAttrRef = useRef(new WeakMap());
  // node -> the language code whose text we last WROTE into it; the same for
  // attributes (element -> Map(attrName -> language code)).
  //
  // Without this the translator cannot tell "this node still shows English, the
  // source" from "this node shows GERMAN, because that is what the user picked
  // before". Both look identical to a lookup: a miss. And a miss leaves the node
  // alone — so anything the newly picked language has no cell for keeps the
  // language the user just LEFT.
  //
  // Croatian is where it shows, because Croatian is the only STATIC_ONLY
  // language (:27): every other language posts its misses to the translation
  // service, whose answer covers the gap. Croatian skips that call, so a miss
  // has nothing behind it. Measured against the staging API on 2026-08-11,
  // German -> Croatian, reading the translator's own state per node:
  //
  //   now="Guten Nachmittag"                        storedOriginal="QA"
  //   now="Verwaltung der Qualitätssicherungsagentur" storedOriginal="QA AgencyAdmin"
  //
  // Both sit in the header, both stayed German in an otherwise Croatian page.
  // (Their German is itself machine-translation nonsense for "QA" — a separate
  // matter, not this one.)
  //
  // With the tag, a miss on a node written in a DIFFERENT language restores that
  // ONE node to its stored English original — English being the app's declared
  // fallback everywhere else (i18n.js fallbackLng). Deliberately per node, and
  // deliberately NOT a global restoreOriginalContent() on every switch: that
  // wipes originalTextMapRef/translatedNodesRef while other runs still hold
  // references into them, and if the following run is then dropped for any
  // reason the whole page is left sitting in English.
  const nodeLanguageRef = useRef(new WeakMap());
  const attrLanguageRef = useRef(new WeakMap());

  // Load cached data on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_KEY);
    const savedCache = localStorage.getItem(CACHE_KEY);
    const savedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    const savedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    // Check cache validity
    const now = new Date().getTime();
    const cacheAge = savedTimestamp ? (now - parseInt(savedTimestamp)) / (1000 * 60 * 60) : Infinity;
    const isCacheValid = savedVersion === CACHE_VERSION && cacheAge < CACHE_EXPIRY_HOURS;
    
    if (savedLanguage) {
      const normalized = savedLanguage.toLowerCase();
      setCurrentLanguage(normalized);
    }
    
    if (savedCache && isCacheValid) {
      try {
        const parsedCache = JSON.parse(savedCache);
        translationCacheRef.current = parsedCache;
        setTranslationCache(parsedCache);
      } catch (error) {
        console.error('Error parsing translation cache:', error);
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      }
    } else {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      localStorage.removeItem(CACHE_VERSION_KEY);
    }
    
    isInitializedRef.current = true;
  }, []);

  // Fetch available languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      const result = await translationService.getLanguages();
      if (result.success && result.data && result.data.length > 0) {
        const mappedLanguages = mapLanguageResponse(result.data);
        // Ensure Croatian appears even if backend does not provide it
        const hasHR = mappedLanguages.some((lang) => (lang.language || '').toLowerCase() === 'hr');
        const withCroatian = hasHR ? mappedLanguages : [...mappedLanguages, { language: 'HR', name: 'Croatian' }];
        setAvailableLanguages(withCroatian);
      } else {
        console.error('Failed to fetch languages:', result.error);
        const popular = getPopularLanguages();
        const hasHR = popular.some((lang) => (lang.language || '').toLowerCase() === 'hr');
        const withCroatian = hasHR ? popular : [...popular, { language: 'HR', name: 'Croatian' }];
        setAvailableLanguages(withCroatian);
      }
    };

    fetchLanguages();
  }, []);

  // Save cache with version and timestamp
  const saveCache = useCallback((cache) => {
    try {
      const cacheEntries = Object.entries(cache);
      if (cacheEntries.length > MAX_CACHE_SIZE) {
        const sortedEntries = cacheEntries.sort();
        const limitedCache = Object.fromEntries(sortedEntries.slice(-MAX_CACHE_SIZE));
        localStorage.setItem(CACHE_KEY, JSON.stringify(limitedCache));
      } else {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      }
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
      localStorage.setItem(CACHE_TIMESTAMP_KEY, new Date().getTime().toString());
    } catch (error) {
      console.error('Error saving translation cache:', error);
    }
  }, []);

  // Save cache whenever it changes
  useEffect(() => {
    if (Object.keys(translationCache).length > 0) {
      saveCache(translationCache);
    }
  }, [translationCache, saveCache]);

  // Save language to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    currentLanguageRef.current = currentLanguage;
  }, [currentLanguage]);

  const ensureOriginalStored = useCallback((node) => {
    if (!node) return;
    if (!originalTextMapRef.current.has(node)) {
      originalTextMapRef.current.set(node, node.textContent);
      translatedNodesRef.current.add(node);
    }
  }, []);

  // Remember which language we just wrote into this node.
  const markNodeLanguage = useCallback((node, language) => {
    if (node) nodeLanguageRef.current.set(node, language);
  }, []);

  // The new language has nothing for this node. If what it currently shows
  // belongs to ANOTHER language we wrote earlier, put the English source back —
  // English is the declared fallback, the language the user just left is not.
  // A node we never wrote still holds its own English source and is untouched.
  const revertForeignLanguageNode = useCallback((node, targetLanguage) => {
    const writtenLanguage = nodeLanguageRef.current.get(node);
    if (!writtenLanguage || writtenLanguage === targetLanguage) return;
    const originalText = originalTextMapRef.current.get(node);
    if (originalText !== undefined && node.isConnected) {
      node.textContent = originalText;
    }
    nodeLanguageRef.current.delete(node);
  }, []);

  // Generate cache key with hash for long texts
  const getCacheKey = useCallback((text, language) => {
    if (text.length > 100) {
      const hash = text.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return `${language}_${Math.abs(hash)}`;
    }
    return `${text}_${language}`;
  }, []);

  // Enhanced text node extraction
  const extractTextNodes = useCallback((priorityCritical = false, rootElement = document.body) => {
    const textNodes = [];
    
    const walker = document.createTreeWalker(
      rootElement,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          
          const tagName = parent.tagName.toLowerCase();
          if (['script', 'style', 'noscript', 'iframe', 'object', 'svg'].includes(tagName)) {
            return NodeFilter.FILTER_REJECT;
          }

          if (parent.getAttribute('data-no-translate') === 'true') {
            return NodeFilter.FILTER_REJECT;
          }
          
          // Use original English text if we have it stored; otherwise use current text
          const text = (originalTextMapRef.current.get(node) ?? node.textContent).trim();
          if (!text || text.length < 2) {
            return NodeFilter.FILTER_REJECT;
          }
          
          if (/^\d+$/.test(text) || 
              /^[\w.-]+@[\w.-]+\.\w+$/.test(text) || 
              /^(https?:\/\/|www\.)/.test(text)) {
            return NodeFilter.FILTER_REJECT;
          }

          if (priorityCritical) {
            const criticalTags = ['h1', 'h2', 'h3', 'nav', 'button', 'a'];
            const isCritical = criticalTags.includes(tagName) || 
                              parent.closest('nav, button, [role="button"], .btn, a');
            if (!isCritical) return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      const originalText = (originalTextMapRef.current.get(node) ?? node.textContent).trim();
      const rect = node.parentElement.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      
      textNodes.push({
        node,
        originalText,
        isVisible,
        isCritical: priorityCritical
      });
    }

    return textNodes;
  }, []);

  // Process batch translation
  const processBatchTranslation = useCallback(async (textNodes, targetLanguage, isCritical) => {
    const normalizedTarget = (targetLanguage || '').toLowerCase();
    const isTargetActive = () => (currentLanguageRef.current || '').toLowerCase() === normalizedTarget;

    if (normalizedTarget === 'en' || !isTargetActive()) {
      return true;
    }

    const textsToTranslate = [];
    const translationMap = new Map();
    let hadFailure = false;
    const newTranslations = {};

    textNodes.forEach(({ node, originalText, isVisible }) => {
      if (!isCritical && !isVisible) {
        // Out of sight is not out of scope when the node still shows a language
        // the user has LEFT. The header carries two copies of the navigation —
        // the top bar and the one inside the mega-menu — and only one of them
        // has a box at any width. The hidden copy was skipped here, kept its old
        // text, and was shown again the moment the menu was toggled: measured at
        // 375 against the staging API, German -> Croatian left "Anfragen /
        // Meine Spieler / Posteingang" standing next to their Croatian twins,
        // always exactly one switch behind.
        //
        // Translating invisible nodes is deliberately NOT what happens here —
        // that gate is a performance decision and it stays. Only the stale text
        // is taken back to English; the next run that sees the node with a box
        // resolves it properly.
        revertForeignLanguageNode(node, normalizedTarget);
        return;
      }

      const cacheKey = getCacheKey(originalText, normalizedTarget);
      // 1) Static translation override via i18next
      let staticTx = getStaticTranslation(i18n, originalText, normalizedTarget);
      if (!staticTx) {
        // Try placeholder/template-based matching
        staticTx = getStaticTemplateMatch(i18n, originalText, normalizedTarget);
      }
      if (staticTx) {
        if (!isTargetActive()) {
          return;
        }
        ensureOriginalStored(node);
        node.textContent = staticTx;
        markNodeLanguage(node, normalizedTarget);
        newTranslations[cacheKey] = staticTx;
        return;
      }

      if (translationCacheRef.current[cacheKey]) {
        if (!isTargetActive()) {
          return;
        }
        ensureOriginalStored(node);
        node.textContent = translationCacheRef.current[cacheKey];
        markNodeLanguage(node, normalizedTarget);
      } else {
        // A miss for the NEW language. Whatever this node still shows from an
        // OLDER language has to go now: for a STATIC_ONLY language nothing else
        // is ever coming, and for the rest the API answer is seconds away at
        // best — English in the meantime, never the language just left.
        revertForeignLanguageNode(node, normalizedTarget);
        if (!translationMap.has(originalText)) {
          translationMap.set(originalText, []);
          textsToTranslate.push(originalText);
        }
      }


      if (!translationCacheRef.current[cacheKey]) {
        if (!translationMap.get(originalText)) {
          translationMap.set(originalText, []);
        }
        translationMap.get(originalText).push({ node, cacheKey });
      }
    });

    // Persist any newly applied static translations into cache upfront
    if (Object.keys(newTranslations).length > 0) {
      translationCacheRef.current = { ...translationCacheRef.current, ...newTranslations };
      setTranslationCache(prev => ({ ...prev, ...newTranslations }));
    }

    // If language is static-only, skip calling dynamic translation API
    if (STATIC_ONLY_LANGS.has(normalizedTarget)) {
      return true;
    }

    if (!isTargetActive()) {
      return true;
    }

    const batches = [];
    for (let i = 0; i < textsToTranslate.length; i += MAX_BATCH_SIZE) {
      batches.push(textsToTranslate.slice(i, i + MAX_BATCH_SIZE));
    }

    for (const batch of batches) {
      if (!isTargetActive()) {
        return true;
      }
      try {
        const result = await translationService.translateTexts(batch, targetLanguage);
        if (!isTargetActive()) {
          return true;
        }
        
        if (result.success) {
          const apiTranslations = {};
          
          result.data.forEach((translatedText, index) => {
            const originalText = batch[index];
            const translation = typeof translatedText === 'string' ? translatedText : translatedText.translated || translatedText;
            const cacheKey = getCacheKey(originalText, normalizedTarget);
            
            apiTranslations[cacheKey] = translation;

            const nodeData = translationMap.get(originalText) || [];
            nodeData.forEach(({ node }) => {
              if (!isTargetActive()) {
                return;
              }
              ensureOriginalStored(node);
              node.textContent = translation;
              markNodeLanguage(node, normalizedTarget);
            });
          });

          translationCacheRef.current = { ...translationCacheRef.current, ...apiTranslations };
          setTranslationCache(prev => ({ ...prev, ...apiTranslations }));
        } else {
          hadFailure = true;
        }
      } catch (error) {
        console.error('Batch translation failed:', error);
        hadFailure = true;
      }
    }

    return !hadFailure;
  }, [ensureOriginalStored, getCacheKey, markNodeLanguage, revertForeignLanguageNode]);

  // Translate text-bearing attributes (placeholder/title/aria-label/alt) using the same
  // static bundle + cache lookup as text nodes; lookup misses stay untouched (English)
  const translateAttributes = useCallback((targetLanguage, rootElement = document.body) => {
    const normalizedTarget = (targetLanguage || '').toLowerCase();
    if (normalizedTarget === 'en') return;
    if ((currentLanguageRef.current || '').toLowerCase() !== normalizedTarget) return;

    const selector = TRANSLATABLE_ATTRIBUTES.map((attr) => `[${attr}]`).join(',');
    const elements = rootElement.querySelectorAll(selector);

    elements.forEach((element) => {
      if (element.closest('script, style, noscript, iframe, object, svg')) return;
      if (element.closest('[data-no-translate="true"]')) return;
      if (element.tagName.toLowerCase() === 'input' && (element.getAttribute('type') || '').toLowerCase() === 'hidden') {
        return;
      }

      TRANSLATABLE_ATTRIBUTES.forEach((attr) => {
        if (!element.hasAttribute(attr)) return;

        const currentValue = element.getAttribute(attr);
        const written = lastWrittenAttrRef.current.get(element);
        const isOurWrite = !!written && written.get(attr) === currentValue;
        let originals = originalAttrMapRef.current.get(element);

        // A value we did not write ourselves is app-authored English – (re)capture it
        // as the original so re-renders never get translated from a translated value
        if (!isOurWrite) {
          if (!originals) {
            originals = new Map();
            originalAttrMapRef.current.set(element, originals);
          }
          originals.set(attr, currentValue);
        }

        const originalValue = originals && originals.has(attr) ? originals.get(attr) : currentValue;
        const text = (originalValue || '').trim();
        if (!text || text.length < 2) return;

        if (/^\d+$/.test(text) ||
            /^[\w.-]+@[\w.-]+\.\w+$/.test(text) ||
            /^(https?:\/\/|www\.)/.test(text)) {
          return;
        }

        let staticTx = getStaticTranslation(i18n, text, normalizedTarget);
        if (!staticTx) {
          staticTx = getStaticTemplateMatch(i18n, text, normalizedTarget);
        }
        const translation = staticTx || translationCacheRef.current[getCacheKey(text, normalizedTarget)];

        let writtenMap = written;
        const ensureWrittenMap = () => {
          if (!writtenMap) {
            writtenMap = new Map();
            lastWrittenAttrRef.current.set(element, writtenMap);
          }
          return writtenMap;
        };
        let attrLanguages = attrLanguageRef.current.get(element);

        if (!translation) {
          // Same rule as for text nodes: a miss must not leave the PREVIOUS
          // language's wording sitting in the attribute. Put the English source
          // back, and record it as our own write so the MutationObserver does
          // not mistake it for app-authored content.
          const writtenLanguage = attrLanguages && attrLanguages.get(attr);
          if (writtenLanguage && writtenLanguage !== normalizedTarget && originalValue !== currentValue) {
            ensureWrittenMap().set(attr, originalValue);
            element.setAttribute(attr, originalValue);
            attrLanguages.delete(attr);
          }
          return;
        }
        if (translation === currentValue) return;

        if (!attrLanguages) {
          attrLanguages = new Map();
          attrLanguageRef.current.set(element, attrLanguages);
        }
        attrLanguages.set(attr, normalizedTarget);
        ensureWrittenMap().set(attr, translation);
        element.setAttribute(attr, translation);
      });
    });
  }, [getCacheKey]);

  // Main translation function
  const translatePage = useCallback(async (targetLanguage, progressive = true, rootElement = document.body, options = {}) => {
    const { force = false } = options;
    const lang = (targetLanguage || '').toLowerCase();
    if (lang === 'en') {
      return;
    }

    // Guard: throttle and prevent overlapping runs unless forced
    if (!force) {
      if (isTranslatingRef.current) return;
      const now = Date.now();
      if (now - lastRunAtRef.current < MIN_RUN_INTERVAL_MS) return;
    }

    // Slot discipline (see scopedDebounceTimeoutRef): a full-page call owns —
    // and may cancel — everything; a scoped call only ever replaces the
    // pending SCOPED run, never a pending full-page one.
    const isFullPage = !rootElement || rootElement === document.body;
    if (isFullPage) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (scopedDebounceTimeoutRef.current) {
        clearTimeout(scopedDebounceTimeoutRef.current);
        scopedDebounceTimeoutRef.current = null;
      }
    } else if (scopedDebounceTimeoutRef.current) {
      clearTimeout(scopedDebounceTimeoutRef.current);
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(async () => {
        const pathKey = `${window.location.pathname}|${lang}`;
        if (!force && failedTranslationKeysRef.current.has(pathKey)) {
          resolve();
          return;
        }

        isTranslatingRef.current = true;
        setIsLoading(true);
        setTranslationProgress(0);

        let anyFailure = false;

        try {
          if (progressive) {
            const criticalNodes = extractTextNodes(true, rootElement);
            if (criticalNodes.length > 0) {
              const okCritical = await processBatchTranslation(criticalNodes, targetLanguage, true);
              if (!okCritical) anyFailure = true;
              setTranslationProgress(30);
            }
          }

          const allNodes = extractTextNodes(false, rootElement);
          const okAll = await processBatchTranslation(allNodes, lang, false);
          if (!okAll) anyFailure = true;
          // Attribute pass rides the same cycle as the text-node scan
          translateAttributes(lang, rootElement);
          setTranslationProgress(100);

        } catch (error) {
          console.error('Error during translation:', error);
          anyFailure = true;
        } finally {
          setIsLoading(false);
          setTranslationProgress(0);
          isTranslatingRef.current = false;
          if (isFullPage) {
            // Only a full-page run arms the throttle and the per-path failure
            // suppression. A widget-scoped run saying "I just ran" must not
            // silence the next whole-page pass (that was how the top-bar
            // navigation stayed untranslated while the widget kept lapping),
            // and a failure inside one widget subtree is no verdict on the
            // page.
            lastRunAtRef.current = Date.now();
            if (anyFailure) {
              failedTranslationKeysRef.current.add(pathKey);
            } else {
              failedTranslationKeysRef.current.delete(pathKey);
            }
          }
          resolve();
        }
      }, DEBOUNCE_DELAY);
      if (isFullPage) {
        debounceTimeoutRef.current = timeoutId;
      } else {
        scopedDebounceTimeoutRef.current = timeoutId;
      }
    });
  }, [extractTextNodes, processBatchTranslation, translateAttributes]);

  // Smart page content detection
  const waitForContent = useCallback(async (maxWaitTime = 5000) => {
    const startTime = Date.now();
    const checkInterval = 100;
    
    while (Date.now() - startTime < maxWaitTime) {
      const textNodes = extractTextNodes(false);
      const meaningfulContent = textNodes.filter(({ originalText }) => 
        originalText.length > 3 && !/^(loading|wait|please)/i.test(originalText)
      );
      
      if (meaningfulContent.length > 5) { // Found substantial content
        return true;
      }
      
      await new Promise(resolve => setTimeout(resolve, checkInterval));
    }
    
    return false; // Timeout reached
  }, [extractTextNodes]);

  // Enhanced navigation handling
  const handlePageTranslation = useCallback(async () => {
    if ((currentLanguage || '').toLowerCase() === 'en' || !isInitializedRef.current) return;
    
    console.log(`🌐 Translation: Handling page translation for ${location.pathname} in ${currentLanguage}`);
    
    // Clear any previous failure suppressions on navigation
    failedTranslationKeysRef.current.clear();
    
    // Clear any existing timeout
    if (pageTranslationTimeoutRef.current) {
      clearTimeout(pageTranslationTimeoutRef.current);
    }
    
    // Wait for content to load, then translate
    const translateWhenReady = async () => {
      console.log('🌐 Translation: Waiting for content to load...');
      
      // Wait for meaningful content
      await waitForContent(3000);
      
      console.log('🌐 Translation: Content detected, starting translation...');
      await translatePage(currentLanguage, true);
      
      lastTranslatedPathRef.current = location.pathname;
      console.log('🌐 Translation: Page translation completed');
    };
    
    // Only a single debounced translate attempt is necessary
    pageTranslationTimeoutRef.current = setTimeout(translateWhenReady, 200);
  }, [currentLanguage, location.pathname, waitForContent, translatePage]);

  // Navigation change handler
  useEffect(() => {
    if (lastTranslatedPathRef.current !== location.pathname) {
      handlePageTranslation();
    }
  }, [location.pathname, handlePageTranslation]);

  // Language change handler
  useEffect(() => {
    if (!isInitializedRef.current) return;
    
    console.log(`🌐 Translation: Language changed to ${currentLanguage}`);
    
    if ((currentLanguage || '').toLowerCase() !== 'en') {
      // Force immediate translation on language change
      // Reset failure suppressions when language changes
      failedTranslationKeysRef.current.clear();
      setTimeout(() => {
        translatePage(currentLanguage, true);
      }, 100);
    }
  }, [currentLanguage, translatePage]);

  // Enhanced MutationObserver
  useEffect(() => {
    if ((currentLanguage || '').toLowerCase() === 'en') return;

    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }

    mutationObserverRef.current = new MutationObserver((mutations) => {
      let hasNewTextContent = false;
      let addedElementsCount = 0;
      let hasAttributeChange = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              addedElementsCount++;
              if (node.textContent && node.textContent.trim().length > 10) {
                hasNewTextContent = true;
              }
            } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 2) {
              hasNewTextContent = true;
            }
          });
        } else if (mutation.type === 'attributes') {
          // Ignore mutations caused by our own attribute writes to prevent self-trigger loops
          const written = lastWrittenAttrRef.current.get(mutation.target);
          const currentValue = mutation.target.getAttribute(mutation.attributeName);
          if (!written || written.get(mutation.attributeName) !== currentValue) {
            hasAttributeChange = true;
          }
        }
      });

      // Only trigger if substantial new content was added – debounce to collapse bursts
      if (((hasNewTextContent && addedElementsCount > 2) || hasAttributeChange) && !isTranslatingRef.current) {
        if (mutationDebounceRef.current) {
          clearTimeout(mutationDebounceRef.current);
        }
        mutationDebounceRef.current = setTimeout(() => {
          console.log('🌐 Translation: MutationObserver debounced translation trigger');
          translatePage(currentLanguage, false);
        }, 500);
      }
    });

    mutationObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: false, // Disable to reduce noise
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES
    });

    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
    };
  }, [currentLanguage, translatePage]);

  // Manual trigger for new content
  const translateNewContent = useCallback(async (selector = null) => {
    if ((currentLanguage || '').toLowerCase() === 'en') return;
    if (isTranslatingRef.current) return;

    let rootElement = document.body;
    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        rootElement = element;
      }
    }

    console.log(`🌐 Translation: Manual translation triggered for ${selector || 'entire page'}`);
    await translatePage(currentLanguage, false, rootElement);
  }, [currentLanguage, translatePage]);

  const restoreOriginalContent = useCallback(() => {
    translatedNodesRef.current.forEach((node) => {
      const originalText = originalTextMapRef.current.get(node);
      if (originalText !== undefined) {
        if (node.isConnected) {
          node.textContent = originalText;
        }
        originalTextMapRef.current.delete(node);
      }
    });
    translatedNodesRef.current.clear();
    originalTextMapRef.current.clear();
    originalAttrMapRef.current.forEach((attrValues, element) => {
      if (element.isConnected) {
        attrValues.forEach((originalValue, attr) => {
          element.setAttribute(attr, originalValue);
        });
      }
      lastWrittenAttrRef.current.delete(element);
    });
    originalAttrMapRef.current.clear();
    // Nothing carries a language any more — the DOM is back to its English
    // source. (WeakMaps have no clear(); a fresh one is the equivalent.)
    nodeLanguageRef.current = new WeakMap();
    attrLanguageRef.current = new WeakMap();
    setIsLoading(false);
    setTranslationProgress(0);
  }, []);

  // Function to change language
  const changeLanguage = useCallback(async (languageCode) => {
    const normalized = (languageCode || '').toLowerCase();
    if (normalized === currentLanguage) return;

    console.log(`🌐 Translation: Changing language from ${currentLanguage} to ${normalized}`);
    // Reset suppression on explicit language change
    failedTranslationKeysRef.current.clear();
    setCurrentLanguage(normalized);
    // Persist selected language immediately so reloads pick up the new value
    localStorage.setItem(LANGUAGE_KEY, normalized);
    // Sync i18next current language for components that may opt-in later
    try {
      const i18nLang = normalizeLang(normalized);
      if (i18n && typeof i18n.changeLanguage === 'function') {
        i18n.changeLanguage(i18nLang);
      }
    } catch (e) {}
    
    if (normalized !== 'en') {
      // Small delay to let state update
      setTimeout(() => {
        // force: an explicit language change must never lose to the throttle.
        //
        // translatePage drops any UNFORCED run that starts while another one is
        // in flight, or within MIN_RUN_INTERVAL_MS (1.5s) of the previous one
        // finishing (:424-428). That throttle exists for the MutationObserver's
        // background churn — not for a choice the user just made. This call went
        // through unforced (the `true` here is `progressive`, the THIRD
        // positional argument, not `force`), so a switch made within ~1.4s of
        // the previous run landing was swallowed with nothing left to retry it:
        // the page kept the old language while the selector and
        // `selected_language` already said the new one, and it stayed that way
        // until some unrelated DOM mutation happened along.
        //
        // That is why the FIRST switch after a page load always worked and every
        // further one appeared dead (Marin, 2026-08-11: "sobald der erste
        // wechsel passiert ist bleibt danach hängen!") — lastRunAtRef starts at
        // 0, so the first switch cannot be inside the window.
        //
        // Measured against the staging API on 2026-08-11, English -> German ->
        // Croatian: `DROPPED@427-throttle lang=hr sinceLastRun=662`, twice (this
        // call and the [currentLanguage] effect's), then twenty seconds of a
        // German page under a Croatian flag.
        translatePage(normalized, true, document.body, { force: true });
      }, 100);
    } else {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      if (scopedDebounceTimeoutRef.current) {
        clearTimeout(scopedDebounceTimeoutRef.current);
        scopedDebounceTimeoutRef.current = null;
      }
      if (pageTranslationTimeoutRef.current) {
        clearTimeout(pageTranslationTimeoutRef.current);
        pageTranslationTimeoutRef.current = null;
      }
      if (mutationDebounceRef.current) {
        clearTimeout(mutationDebounceRef.current);
        mutationDebounceRef.current = null;
      }
      isTranslatingRef.current = false;
      restoreOriginalContent();
    }
  }, [currentLanguage, restoreOriginalContent, translatePage]);

  // Clear cache function
  const clearCache = useCallback(() => {
    translationCacheRef.current = {};
    setTranslationCache({});
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    localStorage.removeItem(CACHE_VERSION_KEY);
  }, []);

  // Listen for API idle events to translate freshly rendered data
  useEffect(() => {
    if ((currentLanguage || '').toLowerCase() === 'en') return;

    const handler = () => {
      console.log('🌐 Translation: apiIdle event detected, translating new content');
      translatePage(currentLanguage, false);
    };

    const detach = onApiIdle(handler);

    return () => {
      detach();
    };
  }, [currentLanguage, translatePage]);

  const value = useMemo(() => ({
    currentLanguage,
    availableLanguages,
    isLoading,
    translationProgress,
    translationCache,
    changeLanguage,
    translatePage,
    translateNewContent,
    clearCache,
    cacheSize: Object.keys(translationCache).length
  }), [
    currentLanguage,
    availableLanguages,
    isLoading,
    translationProgress,
    translationCache,
    changeLanguage,
    translatePage,
    translateNewContent,
    clearCache
  ]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}; 